import React, { useEffect } from 'react';
import { StyleSheet, Text, View, FlatList } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useParams } from 'react-router-dom';

import { usePlusContractQuery } from 'apollo/queries/autogenerate/hooks';
import { PlusContract } from 'apollo/queries/autogenerate/schemas';
import Loading from 'shared/modules/common/components/atoms/Loading';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import { ACTION, SCREEN } from 'shared/modules/usage/constants/mixpanel';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import Mixpanel from 'shared/vendor/Mixpanel';
import testID from 'utils/test-id';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';

export default function PlusContractDetailScreen() {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: plusContractQuery, loading: isLoading } = usePlusContractQuery({
    variables: {
      id: id as string,
    },
    fetchPolicy: 'cache-and-network',
  });
  const contract = plusContractQuery?.plusContract;

  useEffect(() => {
    const eventName = `${SCREEN.PLUS_CONTRACT} - ${ACTION.PAGE_VIEW}`;

    Mixpanel.timeEvent(eventName);

    return () => Mixpanel.trackEvent(eventName);
  }, []);

  if (isLoading && !contract) {
    return <Loading id="loading-indicator" />;
  }

  if (!contract) {
    return null;
  }

  const contractedAt = contract.contractedAt ?? contract.createdAt;

  const data: Array<Pair> = [
    {
      key: '契約日',
      value: formatYearMonthDate(contractedAt, 'en'),
    },
    { key: '分割回数', value: '1回払い' },
    {
      key: '支払日',
      value: contract.dueAt ? formatYearMonthDate(contract.dueAt, 'en') : '-',
    },
    { key: '実質年率', value: '-' },
    { key: '契約金額', value: formatToCurrency(contract.amount) },
    { key: '支払金額', value: formatToCurrency(contract.amount) },
    { key: '分割支払手数料', value: '-' },
    { key: '支払総額', value: formatToCurrency(contract.amount) },
  ];

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        <NavigationBar
          title="超あと払いの申込内容"
          onBackButtonPress={() => navigate(-1)}
          id="navigation-bar"
        />
        <FlatList
          data={data}
          ListHeaderComponent={() => <Header contract={contract} />}
          renderItem={(item) => {
            return (
              <Item
                pair={item.item}
                isTop={item.index === 0}
                isBottom={item.index === data.length - 1}
              />
            );
          }}
          ItemSeparatorComponent={() => (
            <View style={styles.separatorContainer}>
              <View style={styles.separator} />
            </View>
          )}
        />
      </View>
    </View>
  );
}

const Header = ({ contract }: { contract: PlusContract }) => {
  return (
    <View style={styles.headerMargin}>
      <View style={styles.headerRow}>
        <Text style={styles.headerText}>支払金額</Text>
        <Text style={[styles.headerText, styles.headerValue]}>
          {formatToCurrency(contract.amount)}
        </Text>
      </View>
      <View style={styles.headerRow}>
        <Text style={styles.headerText}>支払期日</Text>
        <Text
          style={[styles.headerText, styles.headerValue]}
          {...testID('due-at')}
        >
          {contract.dueAt ? formatYearMonthDate(contract.dueAt, 'en') : '-'}
        </Text>
      </View>
    </View>
  );
};

type Pair = { key: string; value: string };

const Item = ({
  pair,
  isTop,
  isBottom,
}: {
  pair: Pair;
  isTop: boolean;
  isBottom: boolean;
}) => {
  const topRadius = isTop ? 8 : 0;
  const bottomRadius = isBottom ? 8 : 0;

  return (
    <View
      style={[
        styles.itemContainer,
        {
          borderTopStartRadius: topRadius,
          borderTopEndRadius: topRadius,
          borderBottomStartRadius: bottomRadius,
          borderBottomEndRadius: bottomRadius,
        },
      ]}
    >
      <Text style={styles.itemKey}>{pair.key}</Text>
      <Text style={styles.itemValue}>{pair.value}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    alignItems: 'center',
    padding: 16,
  },
  screenBigScreen: {
    padding: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  headerRow: {
    flex: 1,
    flexDirection: 'row',
  },
  headerText: {
    ...typography.title03,
    fontSize: 16,
    fontWeight: '700',
    color: colors.greyDark,
  },
  headerValue: {
    flex: 1,
    textAlign: 'right',
  },
  headerMargin: {
    marginBottom: 24,
  },
  itemContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    padding: 16,
  },
  itemKey: {
    ...typography.caption02,
    fontSize: 13,
    fontWeight: '400',
    color: colors.greyDark,
  },
  itemValue: {
    ...typography.title02,
    flex: 1,
    fontSize: 13,
    fontWeight: '400',
    color: colors.greyDark,
    textAlign: 'right',
  },
  separatorContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  separator: {
    flex: 1,
    height: 1,
    marginHorizontal: 16,
    backgroundColor: colors.greySuperLight,
  },
});
